import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Stack,
  Button,
  Typography,
} from '@mui/material'
import { useState } from 'react'

type FilterMultiSelectOption<T> = {
  value: T
  label: string
}

type FilterMultiSelectProps<T> = {
  label: string
  options: FilterMultiSelectOption<T>[]
  defaultValue?: T[]
  onSubmit: (params: { value: T[]; label: string }) => void
  onClose: () => void
}

export const FilterMultiSelect = <T extends string | number>({
  label,
  options,
  defaultValue = [],
  onSubmit,
  onClose,
}: FilterMultiSelectProps<T>) => {
  const [selectedValues, setSelectedValues] = useState<T[]>(defaultValue)

  const handleToggle = (value: T) => {
    setSelectedValues((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value)
      } else {
        return [...prev, value]
      }
    })
  }

  const handleSubmit = () => {
    if (selectedValues.length > 0) {
      onSubmit({
        value: selectedValues,
        label: `${selectedValues.length} selected`,
      })
    }
  }

  const handleClose = () => {
    onClose()
  }

  const handleSelectAll = () => {
    setSelectedValues(options.map((opt) => opt.value))
  }

  const handleClearAll = () => {
    setSelectedValues([])
  }

  return (
    <Stack sx={{ py: 2, px: 2 }}>
      <FormControl component="fieldset" sx={{ gap: 1, flexDirection: 'column' }}>
        <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between">
          <FormLabel component="legend">
            <Typography variant="subtitle2">{label}</Typography>
          </FormLabel>

          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button size="small" onClick={handleClearAll}>
              Clear All
            </Button>
          </Stack>
        </Stack>

        <Stack sx={{ height: 300, overflowY: 'auto', flexWrap: 'nowrap', my: 1 }}>
          {options.map((option) => (
            <FormControlLabel
              key={String(option.value)}
              control={
                <Checkbox
                  size="small"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => handleToggle(option.value)}
                  sx={{ p: 0.5, mr: 0.5 }}
                />
              }
              label={<Typography variant="body2">{option.label}</Typography>}
              sx={{ margin: 0 }}
            />
          ))}
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap sx={{ alignSelf: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={selectedValues.length === 0}
          >
            Apply
          </Button>
          <Button variant="outlined" size="small" onClick={handleClose}>
            Close
          </Button>
        </Stack>
      </FormControl>
    </Stack>
  )
}
